/**
 * TclMappingView — Stewardship · TCL Mapping sub-tab (iter86.21.2).
 *
 * Step 3: the values-registry consolidation proposal (35 checks → 12
 * standards) shown as a diff; nothing commits until the steward approves.
 * Step 4: per-standard / per-obligation `expresses_tcl` proposals with
 * Accept / Override / Reject. TCL statements are cited inline so the
 * steward never dispositions from memory. `product-specific` is a final,
 * legitimate state — not a warning.
 */
import { useEffect, useState } from "react";
import { AlertTriangle, Check, GitBranch, History, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const dispPill = (d) => ({
    pending: "bg-amber-900/40 text-amber-200 border-amber-700",
    accepted: "bg-emerald-900/40 text-emerald-200 border-emerald-700",
    overridden: "bg-sky-900/40 text-sky-200 border-sky-700",
    rejected: "bg-rose-900/40 text-rose-200 border-rose-700",
}[d] || "bg-zinc-800 text-zinc-300 border-zinc-600");

// iter86.23 — proposed upstream additions to the TCL registry itself.
function TclAdditionRow({ row, gated, reload }) {
    const [busy, setBusy] = useState(false);
    const pending = row.disposition === "pending";
    const act = async (action) => {
        let note = null;
        if (action === "reject") {
            note = window.prompt(`Reason to reject "${row.proposed_id}" (final — the item will not enter the TCL registry):`);
            if (!note) return;
        } else if (!window.confirm(
            `Accept ${row.proposed_id} into the TCL registry?\n\nThis inserts the item into tcl_registry.md, writes a new history snapshot, and re-proposes ${row.remap_candidates.length} downstream mapping(s) as pending for your disposition. Nothing downstream is auto-remapped.`)) {
            return;
        }
        setBusy(true);
        try {
            const r = await api.tclAdditionDecision({ proposed_id: row.proposed_id, action, note });
            toast.success(action === "accept"
                ? `Accepted ${row.proposed_id} · snapshot ${r.snapshot} · ${r.remap_proposed.length} re-mapping(s) proposed`
                : `Rejected ${row.proposed_id}`);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || `${action} failed`);
        } finally { setBusy(false); }
    };
    return (
        <div className="border border-zinc-800 rounded p-3 space-y-1.5" data-testid={`tcl-addition-row-${row.proposed_id}`}>
            <div className="flex items-center gap-2 flex-wrap">
                <code className="text-xs font-bold text-white">{row.proposed_id}</code>
                <span className={`pill text-[9px] font-bold uppercase border ${row.category === "architecture" ? "bg-cyan-900/40 text-cyan-200 border-cyan-700" : "bg-violet-900/40 text-violet-200 border-violet-700"}`}>{row.category}</span>
                <span className={`pill text-[9px] font-bold uppercase border ${dispPill(row.disposition)}`} data-testid={`tcl-addition-disposition-${row.proposed_id}`}>{row.disposition}</span>
                {row.review_needed && pending && (
                    <span className="pill text-[9px] font-bold uppercase border bg-fuchsia-900/40 text-fuchsia-200 border-fuchsia-700">review needed</span>
                )}
            </div>
            <div className="text-[11px] text-zinc-200 italic">“{row.statement}”</div>
            <div className="text-[10px] text-zinc-500">{row.rationale}</div>
            <div className="text-[10px] text-zinc-600">Source: {row.source}</div>
            {row.remap_candidates.length > 0 && (
                <div className="text-[10px] text-zinc-400" data-testid={`tcl-addition-remap-${row.proposed_id}`}>
                    On accept, re-proposed for your disposition: {row.remap_candidates.map((s) => <code key={s} className="text-cyan-300 mr-1.5">{s}</code>)}
                </div>
            )}
            {row.disposition_note && <div className="text-[10px] text-amber-200/90">Note: {row.disposition_note}</div>}
            {pending && (
                <div className="flex gap-2 pt-1">
                    <button onClick={() => act("accept")} disabled={busy || gated}
                        className="brutal-btn text-[10px] px-3 py-1 bg-emerald-700 text-white disabled:opacity-40"
                        data-testid={`tcl-addition-accept-btn-${row.proposed_id}`}>
                        <Check className="w-3 h-3" /> Accept into TCL registry
                    </button>
                    <button onClick={() => act("reject")} disabled={busy || gated}
                        className="brutal-btn-ghost text-[10px] px-2 py-1 text-rose-300"
                        data-testid={`tcl-addition-reject-btn-${row.proposed_id}`}>
                        <X className="w-3 h-3" /> Reject (final)
                    </button>
                </div>
            )}
        </div>
    );
}

// Layer micro-tags — same treatment as the Ladder view: the reader always
// knows which rung they're looking at. TCL and standard ids are NOT aliases:
// the TCL commitment is the upstream parent a standard expresses.
const LayerTag = ({ layer }) => ({
    tcl: <span className="text-[8px] font-extrabold uppercase tracking-wider text-violet-400 border border-violet-800 rounded px-1 py-px">TCL</span>,
    value: <span className="text-[8px] font-extrabold uppercase tracking-wider text-emerald-400 border border-emerald-800 rounded px-1 py-px">Value</span>,
    standard: <span className="text-[8px] font-extrabold uppercase tracking-wider text-cyan-400 border border-cyan-800 rounded px-1 py-px">Standard</span>,
    obligation: <span className="text-[8px] font-extrabold uppercase tracking-wider text-orange-400 border border-orange-800 rounded px-1 py-px">Obligation</span>,
}[layer]);

function ProposalChips({ row }) {
    const verb = row.disposition === "pending" ? "proposes TCL" : "expresses TCL";
    if (row.proposal_product_specific) {
        return (
            <div className="text-[10px] flex items-center gap-1.5 flex-wrap" data-testid={`tcl-upstream-line-${row.subject}`}>
                <span className="text-zinc-500">→ {verb}:</span>
                <span className="pill text-[9px] font-bold uppercase border bg-zinc-800 text-zinc-200 border-zinc-600" data-testid={`tcl-chip-product-specific-${row.subject}`}>
                    product-specific — no upstream parent
                </span>
            </div>
        );
    }
    return (
        <div className="space-y-1" data-testid={`tcl-upstream-line-${row.subject}`}>
            {row.citations.map((c) => (
                <div key={c.id}>
                    <span className="text-[10px] text-zinc-500">→ {verb}: </span>
                    <LayerTag layer="tcl" />
                    <code className="text-[10px] text-violet-300 ml-1">↑ {c.id}</code>
                    {c.exists
                        ? <div className="text-[10px] text-zinc-500 italic pl-2 border-l border-zinc-700 ml-1 mt-0.5">“{c.statement}”</div>
                        : <span className="text-[10px] text-rose-400 ml-2">⚠ no longer in TCL registry</span>}
                </div>
            ))}
        </div>
    );
}

function OverrideEditor({ row, tclIds, onDone, onCancel }) {
    const [ps, setPs] = useState(row.proposal_product_specific);
    const [sel, setSel] = useState(new Set(row.proposal_tcl));
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    const commit = async () => {
        setBusy(true);
        try {
            await api.tclMappingDisposition({
                subject_type: row.subject_type, subject: row.subject, action: "override",
                product_specific: ps, expresses_tcl: ps ? [] : [...sel], note: note || null,
            });
            toast.success(`Overridden: ${row.subject}`);
            onDone();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Override failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="mt-2 border border-sky-800 rounded p-3 space-y-2 bg-sky-950/20" data-testid={`tcl-override-editor-${row.subject}`}>
            <label className="flex items-center gap-2 text-[11px] text-zinc-300">
                <input type="checkbox" checked={ps} onChange={(e) => setPs(e.target.checked)} className="accent-volt" />
                Product-specific (no TCL parent — final state)
            </label>
            {!ps && (
                <div className="grid sm:grid-cols-2 gap-1">
                    {tclIds.map((t) => (
                        <label key={t} className="flex items-center gap-2 text-[10px] text-zinc-400">
                            <input type="checkbox" checked={sel.has(t)} className="accent-volt"
                                onChange={() => setSel((p) => { const n = new Set(p); n.has(t) ? n.delete(t) : n.add(t); return n; })} />
                            <code>{t}</code>
                        </label>
                    ))}
                </div>
            )}
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)"
                className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-[11px]"
                data-testid={`tcl-override-note-${row.subject}`} />
            <div className="flex gap-2">
                <button onClick={commit} disabled={busy} className="brutal-btn text-[10px] px-3 py-1 bg-sky-600 text-white disabled:opacity-50" data-testid={`tcl-override-commit-${row.subject}`}>Commit override</button>
                <button onClick={onCancel} className="brutal-btn-ghost text-[10px] px-2 py-1">Cancel</button>
            </div>
        </div>
    );
}

function ProposalRow({ row, tclIds, gated, reload, valueGroup }) {
    const [editing, setEditing] = useState(false);
    const [busy, setBusy] = useState(false);
    const pending = row.disposition === "pending";

    const act = async (action) => {
        let note = null;
        if (action === "reject") {
            note = window.prompt(`Reason to send "${row.subject}" back to Emergent:`);
            if (!note) return;
        } else if (action === "reopen") {
            note = window.prompt(`Reason to re-open "${row.subject}"? The committed mapping stands until you re-disposition it.`);
            if (!note) return;
        }
        setBusy(true);
        try {
            await api.tclMappingDisposition({ subject_type: row.subject_type, subject: row.subject, action, note });
            toast.success(`${action === "accept" ? "Accepted" : action === "reopen" ? "Re-opened" : "Rejected"}: ${row.subject}`);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || `${action} failed`);
        } finally { setBusy(false); }
    };

    return (
        <div className="border border-zinc-800 rounded p-3 space-y-1.5" data-testid={`tcl-proposal-row-${row.subject}`}>
            <div className="flex items-center gap-2 flex-wrap">
                <LayerTag layer={row.subject_type === "obligation" ? "obligation" : "standard"} />
                <code className="text-xs font-bold text-white">{row.subject}</code>
                <span className={`pill text-[9px] font-bold uppercase border ${dispPill(row.disposition)}`} data-testid={`tcl-disposition-${row.subject}`}>{row.disposition}</span>
                {row.review_needed && pending && (
                    <span className="pill text-[9px] font-bold uppercase border bg-fuchsia-900/40 text-fuchsia-200 border-fuchsia-700">review needed</span>
                )}
            </div>
            <ProposalChips row={row} />
            {row.subject_type === "standard" && valueGroup && (
                <div className="text-[10px] flex items-center gap-1.5 flex-wrap" data-testid={`tcl-value-line-${row.subject}`}>
                    <span className="text-zinc-500">→ under value:</span>
                    <LayerTag layer="value" />
                    <code className="text-emerald-300">{valueGroup}</code>
                </div>
            )}
            <div className="text-[10px] text-zinc-500">{row.rationale}</div>
            {row.disposition_note && <div className="text-[10px] text-zinc-400">Note: {row.disposition_note}</div>}
            {pending && (
                <div className="flex gap-2 pt-1">
                    <button onClick={() => act("accept")} disabled={busy || gated}
                        title={gated ? "Approve the consolidation first" : ""}
                        className="brutal-btn text-[10px] px-3 py-1 bg-emerald-700 text-white disabled:opacity-40"
                        data-testid={`tcl-accept-btn-${row.subject}`}>
                        <Check className="w-3 h-3" /> Accept
                    </button>
                    <button onClick={() => setEditing((v) => !v)} disabled={busy || gated}
                        className="brutal-btn text-[10px] px-3 py-1 bg-sky-800 text-white disabled:opacity-40"
                        data-testid={`tcl-override-btn-${row.subject}`}>
                        <Pencil className="w-3 h-3" /> Override
                    </button>
                    <button onClick={() => act("reject")} disabled={busy}
                        className="brutal-btn-ghost text-[10px] px-2 py-1 text-rose-300"
                        data-testid={`tcl-reject-btn-${row.subject}`}>
                        <X className="w-3 h-3" /> Reject
                    </button>
                </div>
            )}
            {(row.disposition === "accepted" || row.disposition === "overridden") && !gated && (
                <div className="pt-1">
                    <button onClick={() => act("reopen")} disabled={busy}
                        className="brutal-btn-ghost text-[10px] px-2 py-1 text-amber-300"
                        data-testid={`tcl-reopen-btn-${row.subject}`}>
                        <History className="w-3 h-3" /> Re-open
                    </button>
                </div>
            )}
            {editing && <OverrideEditor row={row} tclIds={tclIds} onCancel={() => setEditing(false)} onDone={() => { setEditing(false); reload(); }} />}
        </div>
    );
}

function TclUpdateCard({ reloadAll }) {
    const [upd, setUpd] = useState(null);
    const [busy, setBusy] = useState(false);
    const load = () => api.tclUpdate().then(setUpd).catch(() => {});
    useEffect(() => { load(); }, []);
    if (!upd) return null;
    if (upd.status === "in-sync") {
        return (
            <div className="text-[10px] text-zinc-600" data-testid="tcl-update-insync">
                TCL registry in sync with snapshot <code>{upd.latest_snapshot}</code> ({upd.snapshot_count} snapshot{upd.snapshot_count === 1 ? "" : "s"} in history).
            </div>
        );
    }
    const ack = async () => {
        if (!window.confirm("Acknowledge this TCL update? A new history snapshot is written and every affected mapping returns to pending for re-review. No TCR standard is modified.")) return;
        setBusy(true);
        try {
            const r = await api.tclUpdateAcknowledge(null);
            toast.success(`Snapshot ${r.snapshot} written · ${r.reopened.length} mapping(s) reopened`);
            load(); reloadAll();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Acknowledge failed");
        } finally { setBusy(false); }
    };
    const d = upd.diff;
    return (
        <div className="border-2 border-fuchsia-800/60 rounded-lg p-4 bg-fuchsia-950/10" data-testid="tcl-update-card">
            <div className="flex items-center gap-2 mb-1">
                <History className="w-4 h-4 text-fuchsia-300" />
                <h3 className="font-bold text-sm text-white uppercase">TCL Registry Update detected</h3>
            </div>
            <div className="text-[11px] text-zinc-400 mb-2">
                The file at <code>/app/memory/tcl_registry.md</code> differs from snapshot <code>{upd.latest_snapshot}</code>.
            </div>
            <div className="space-y-1 text-[10px]">
                {d.added.map((i) => <div key={i} className="text-emerald-300">+ added <code>{i}</code></div>)}
                {d.removed.map((i) => <div key={i} className="text-rose-300">− removed <code>{i}</code></div>)}
                {d.changed.map((c) => (
                    <div key={c.id} className="text-amber-300">~ changed <code>{c.id}</code> ({Object.keys(c.changes).join(", ")})</div>
                ))}
            </div>
            {upd.affected.length > 0 && (
                <div className="text-[10px] text-zinc-400 mt-2" data-testid="tcl-update-affected">
                    Affected TCR mappings (return to pending on acknowledge): {upd.affected.map((a) => a.subject).join(", ")}
                </div>
            )}
            <button onClick={ack} disabled={busy} className="brutal-btn text-[10px] px-3 py-1.5 bg-fuchsia-700 text-white mt-3 disabled:opacity-50" data-testid="tcl-update-ack-btn">
                <Check className="w-3 h-3" /> Acknowledge & snapshot
            </button>
        </div>
    );
}

function DriftWatch({ drift }) {
    if (!drift || drift.clean) {
        return <div className="text-[10px] text-zinc-600" data-testid="tcl-drift-clean">TCL drift watch: clean — no dangling refs, no contradiction warnings (checked {drift?.checked_at}).</div>;
    }
    return (
        <div className="border border-amber-800/60 rounded-lg p-3 bg-amber-950/10 space-y-1.5" data-testid="tcl-drift-warnings">
            <div className="flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-300" />
                <span className="font-bold text-[11px] text-amber-200 uppercase">TCL drift watch — soft warnings, never blocking</span>
            </div>
            {drift.dangling.map((f, i) => (
                <div key={`d${i}`} className="text-[10px] text-rose-300" data-testid={`tcl-drift-dangling-${f.subject}`}>
                    ⚠ <b>{f.subject}</b> references <code>{f.tcl_id}</code> — {f.detail}
                </div>
            ))}
            {drift.contradictions.map((f, i) => (
                <div key={`c${i}`} className="text-[10px] text-amber-300" data-testid={`tcl-drift-contradiction-${f.subject}`}>
                    ⚠ <b>{f.subject}</b> vs <code>{f.tcl_id}</code>: opposing terms [{f.terms.join(", ")}] — {f.detail}
                </div>
            ))}
        </div>
    );
}

function ApprovalCard({ title, subtitle, status, testid, onApprove, children }) {
    const [busy, setBusy] = useState(false);
    const approve = async () => {
        if (!window.confirm(`Approve "${title}"? This commits to the registry file and is audit-logged.`)) return;
        setBusy(true);
        try { await onApprove(); } finally { setBusy(false); }
    };
    return (
        <div className="border-2 border-emerald-900/50 rounded-lg p-4 bg-emerald-950/10" data-testid={testid}>
            <div className="flex items-center gap-2 flex-wrap mb-1">
                <h3 className="font-bold text-sm text-white uppercase">{title}</h3>
                <span className={`pill text-[9px] font-bold uppercase border ${dispPill(status === "approved" ? "accepted" : status)}`}>{status}</span>
            </div>
            <div className="text-[11px] text-zinc-400 mb-2">{subtitle}</div>
            {children}
            {status === "pending" && (
                <button onClick={approve} disabled={busy} className="brutal-btn text-[10px] px-3 py-1.5 bg-emerald-700 text-white mt-3 disabled:opacity-50" data-testid={`${testid}-approve-btn`}>
                    <Check className="w-3 h-3" /> Approve & commit
                </button>
            )}
        </div>
    );
}

// iter86.24 — steward-proposed wording changes to existing TCL items.
function TclAmendmentsSection({ amendments, tclIds, gated, reload }) {
    const [showForm, setShowForm] = useState(false);
    const [tclId, setTclId] = useState("");
    const [field, setField] = useState("statement");
    const [text, setText] = useState("");
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);

    const propose = async () => {
        setBusy(true);
        try {
            const r = await api.tclAmendmentPropose({ tcl_id: tclId, field, proposed_text: text, reason });
            toast.success(`Amendment #${r.num} proposed — ${r.affected.length} downstream mapping(s) would re-open on approval`);
            setShowForm(false); setTclId(""); setText(""); setReason("");
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Propose failed");
        } finally { setBusy(false); }
    };

    const decide = async (row, action) => {
        let note = null;
        if (action === "reject") {
            note = window.prompt(`Reason to reject amendment #${row.num} (final)?`);
            if (!note) return;
        } else if (!window.confirm(
            `Approve amendment #${row.num} to ${row.tcl_id}?\n\nThis rewrites the ${row.field}, bumps last_amended, writes a history snapshot, and re-opens ${row.affected?.length || 0} downstream mapping(s) for your re-review.`)) {
            return;
        }
        setBusy(true);
        try {
            const r = await api.tclAmendmentDecision({ num: row.num, action, note });
            toast.success(action === "approve"
                ? `Amended ${row.tcl_id} · snapshot ${r.snapshot} · ${r.reopened.length} mapping(s) re-opened`
                : `Rejected amendment #${row.num}`);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || `${action} failed`);
        } finally { setBusy(false); }
    };

    return (
        <div className="space-y-2" data-testid="tcl-amendments-section">
            <div className="flex items-center gap-3 flex-wrap">
                <h3 className="font-bold text-sm text-white uppercase">Upstream — TCL amendment proposals</h3>
                <button onClick={() => setShowForm((v) => !v)} disabled={gated}
                    className="brutal-btn text-[10px] px-3 py-1 bg-cyan-800 text-white disabled:opacity-40"
                    data-testid="tcl-amendment-propose-toggle">
                    <Pencil className="w-3 h-3" /> Propose amendment
                </button>
            </div>
            <div className="text-[10px] text-zinc-500">
                Wording changes to existing TCL commitments. Proposing only queues a pending row (git history); approval rewrites the item, snapshots, audits, and re-opens every downstream mapping that references it — the impact set is shown before you approve.
            </div>
            {showForm && (
                <div className="border border-cyan-800 rounded p-3 space-y-2 bg-cyan-950/20" data-testid="tcl-amendment-form">
                    <div className="flex gap-2 flex-wrap">
                        <select value={tclId} onChange={(e) => setTclId(e.target.value)}
                            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-[11px]"
                            data-testid="tcl-amendment-id-select">
                            <option value="">— pick a TCL commitment —</option>
                            {tclIds.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <select value={field} onChange={(e) => setField(e.target.value)}
                            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-[11px]"
                            data-testid="tcl-amendment-field-select">
                            <option value="statement">statement</option>
                            <option value="rationale">rationale</option>
                        </select>
                    </div>
                    <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3}
                        placeholder={`Proposed ${field} text`}
                        className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-[11px]"
                        data-testid="tcl-amendment-text-input" />
                    <input value={reason} onChange={(e) => setReason(e.target.value)}
                        placeholder="Reason (required — lands in the audit log)"
                        className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-[11px]"
                        data-testid="tcl-amendment-reason-input" />
                    <div className="flex gap-2">
                        <button onClick={propose} disabled={busy || !tclId || !text.trim() || !reason.trim()}
                            className="brutal-btn text-[10px] px-3 py-1 bg-cyan-700 text-white disabled:opacity-40"
                            data-testid="tcl-amendment-submit-btn">Queue proposal</button>
                        <button onClick={() => setShowForm(false)} className="brutal-btn-ghost text-[10px] px-2 py-1">Cancel</button>
                    </div>
                </div>
            )}
            {amendments.length === 0 && !showForm && (
                <div className="text-[10px] text-zinc-600" data-testid="tcl-amendments-empty">No amendment proposals yet.</div>
            )}
            {amendments.map((row) => (
                <div key={row.num} className="border border-zinc-800 rounded p-3 space-y-1.5" data-testid={`tcl-amendment-row-${row.num}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] text-zinc-500 font-bold">#{row.num}</span>
                        <code className="text-xs font-bold text-white">{row.tcl_id}</code>
                        <span className="pill text-[9px] font-bold uppercase border bg-zinc-800 text-zinc-300 border-zinc-600">{row.field}</span>
                        <span className={`pill text-[9px] font-bold uppercase border ${dispPill(row.disposition === "approved" ? "accepted" : row.disposition)}`} data-testid={`tcl-amendment-disposition-${row.num}`}>{row.disposition}</span>
                    </div>
                    <div className="text-[11px] text-zinc-200 italic">“{row.proposed_text}”</div>
                    <div className="text-[10px] text-zinc-500">Reason: {row.reason} — {row.proposed_by} @ {row.proposed_at}</div>
                    {row.disposition === "pending" && (
                        <div className="text-[10px] text-amber-200/90" data-testid={`tcl-amendment-impact-${row.num}`}>
                            Impact on approval: {row.affected?.length
                                ? <>re-opens {row.affected.map((a) => <code key={a.subject} className="text-cyan-300 mr-1.5">{a.subject}</code>)}</>
                                : "no downstream mappings reference this id"}
                        </div>
                    )}
                    {row.disposition_note && <div className="text-[10px] text-zinc-400">Note: {row.disposition_note}</div>}
                    {row.disposition === "pending" && (
                        <div className="flex gap-2 pt-1">
                            <button onClick={() => decide(row, "approve")} disabled={busy || gated}
                                className="brutal-btn text-[10px] px-3 py-1 bg-emerald-700 text-white disabled:opacity-40"
                                data-testid={`tcl-amendment-approve-btn-${row.num}`}>
                                <Check className="w-3 h-3" /> Approve & amend
                            </button>
                            <button onClick={() => decide(row, "reject")} disabled={busy || gated}
                                className="brutal-btn-ghost text-[10px] px-2 py-1 text-rose-300"
                                data-testid={`tcl-amendment-reject-btn-${row.num}`}>
                                <X className="w-3 h-3" /> Reject (final)
                            </button>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

export function TclMappingView() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [dataPlane, setDataPlane] = useState(false);

    const load = () => api.tclMapping().then(setData)
        .catch((e) => setError(e?.response?.data?.detail || "Failed to load TCL mapping"));
    useEffect(() => {
        load();
        api.platformEnvironment().then((r) => setDataPlane(r.mode === "data_plane")).catch(() => {});
    }, []);

    if (error) return <div className="text-xs text-rose-300" data-testid="tcl-mapping-error">{error}</div>;
    if (!data) return <p className="text-[11px] text-zinc-500 italic">Loading TCL mapping…</p>;

    const cons = data.consolidation;
    const decide = async (action) => {
        let note = null;
        if (action === "reject") {
            note = window.prompt("Reason for rejecting the consolidation:");
            if (!note) return;
        } else if (!window.confirm("Approve the consolidation? This writes the 12-standard Standards Catalog into values_registry.md (check rows are untouched).")) {
            return;
        }
        setBusy(true);
        try {
            await api.tclMappingConsolidationDecision(action, note);
            toast.success(`Consolidation ${action}d`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Decision failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="space-y-4" data-testid="tcl-mapping-view">
            {dataPlane && (
                <div className="border border-rose-800 rounded p-2.5 bg-rose-950/20 text-[11px] text-rose-200" data-testid="tcl-data-plane-lock-banner">
                    🔒 Data Plane (production) — this canon view is a read-only mirror. Governance writes happen in the preview control plane and flow through git.
                </div>
            )}
            <TclUpdateCard reloadAll={load} />
            <DriftWatch drift={data.drift} />
            {data.tcl_additions?.length > 0 && (
                <div className="space-y-2" data-testid="tcl-additions-section">
                    <h3 className="font-bold text-sm text-white uppercase">Upstream — proposed TCL additions</h3>
                    <div className="text-[10px] text-zinc-500">
                        New commitments proposed for the TCL registry itself. Accept inserts the item into <code>tcl_registry.md</code>, writes an append-only history snapshot, and re-proposes candidate downstream mappings (each returns to pending for your disposition). Reject is final and requires a reason.
                    </div>
                    {data.tcl_additions.map((r) => (
                        <TclAdditionRow key={r.proposed_id} row={r} gated={dataPlane} reload={load} />
                    ))}
                </div>
            )}
            {data.tcl_amendments && (
                <TclAmendmentsSection amendments={data.tcl_amendments} tclIds={data.tcl_ids} gated={dataPlane} reload={load} />
            )}
            {data.value_cascade && (
                <ApprovalCard
                    title="Step A — Standards → Value assignments (cascade)"
                    subtitle="One decision per standard. On approval, every still-pending check inherits its standard's value — the pending classification queue clears in one audited action. Game-specific standards do not ladder up."
                    status={data.value_cascade.status}
                    testid="tcl-value-cascade-card"
                    onApprove={async () => {
                        const r = await api.tclValueCascadeApprove();
                        toast.success(`${r.classified.length} checks classified via cascade`);
                        load();
                    }}
                >
                    <table className="w-full text-[10px]">
                        <tbody>
                            {data.value_cascade.assignments.map((a) => (
                                <tr key={a.standard_id} className="border-t border-zinc-800 align-top">
                                    <td className="pr-2 py-1"><code className="text-cyan-300">{a.standard_id}</code></td>
                                    <td className="pr-2"><code className="text-emerald-300">{a.value_group || "— (game-specific)"}</code></td>
                                    <td className="text-zinc-500">{a.rationale}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </ApprovalCard>
            )}
            {data.value_amendments && (
                <ApprovalCard
                    title="Step B — Value statement amendments"
                    subtitle="Rewords each value as a pure one-sentence belief; the concrete mechanics now live in the Standards Catalog."
                    status={data.value_amendments.status}
                    testid="tcl-value-amendments-card"
                    onApprove={async () => {
                        const r = await api.tclValueAmendmentsApprove();
                        toast.success(`${r.amended.length} value statements amended`);
                        load();
                    }}
                >
                    <div className="space-y-1">
                        {data.value_amendments.amendments.map((a) => (
                            <div key={a.value_group} className="text-[10px]">
                                <code className="text-emerald-300">{a.value_group}</code>
                                <span className="text-zinc-300 ml-2">“{a.statement}”</span>
                            </div>
                        ))}
                    </div>
                </ApprovalCard>
            )}
            <div className="border-2 border-cyan-900/50 rounded-lg p-4 bg-cyan-950/10" data-testid="tcl-consolidation-card">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                    <GitBranch className="w-4 h-4 text-cyan-300" />
                    <h3 className="font-bold text-sm text-white uppercase">Step 3 — Consolidation Proposal</h3>
                    <span className={`pill text-[9px] font-bold uppercase border ${dispPill(cons.status === "approved" ? "accepted" : cons.status)}`} data-testid="tcl-consolidation-status">{cons.status}</span>
                </div>
                <div className="text-[11px] text-zinc-400 mb-2">
                    35 check-level entries → <b>{cons.standards.length} standards</b>. Checks stay as the severity-governed enforcement layer; each standard's Signals column lists the checks that back it. Nothing is deleted.
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-[10px]">
                        <thead><tr className="text-zinc-500 text-left uppercase">
                            <th className="pr-2 py-1">Standard (new)</th><th className="pr-2">Scope</th><th className="pr-2">Statement</th><th>← Signals (existing entries)</th>
                        </tr></thead>
                        <tbody>
                            {cons.standards.map((s) => (
                                <tr key={s.standard_id} className="border-t border-zinc-800 align-top" data-testid={`tcl-cons-row-${s.standard_id}`}>
                                    <td className="pr-2 py-1.5"><code className="text-cyan-300">{s.standard_id}</code></td>
                                    <td className="pr-2 text-zinc-400">{s.scope}</td>
                                    <td className="pr-2 text-zinc-300">{s.statement}</td>
                                    <td className="text-zinc-500">{s.signals.join(", ")}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {cons.status === "pending" && (
                    <div className="flex gap-2 mt-3">
                        <button onClick={() => decide("approve")} disabled={busy} className="brutal-btn text-[10px] px-3 py-1.5 bg-emerald-700 text-white disabled:opacity-50" data-testid="tcl-consolidation-approve-btn">
                            <Check className="w-3 h-3" /> Approve & commit catalog
                        </button>
                        <button onClick={() => decide("reject")} disabled={busy} className="brutal-btn-ghost text-[10px] px-2 py-1.5 text-rose-300" data-testid="tcl-consolidation-reject-btn">
                            <X className="w-3 h-3" /> Reject with note
                        </button>
                    </div>
                )}
                {cons.note && <div className="text-[10px] text-zinc-400 mt-2">Note: {cons.note} — {cons.decided_by} @ {cons.decided_at}</div>}
            </div>

            <div className="space-y-2">
                <h3 className="font-bold text-sm text-white uppercase">Step 4 — Standards → TCL mappings
                    {!cons.applied && <span className="text-[10px] text-amber-300 normal-case font-normal ml-2">(gated until consolidation is approved)</span>}
                </h3>
                {data.standards_proposals.map((r) => (
                    <ProposalRow key={r.subject} row={r} tclIds={data.tcl_ids} gated={!cons.applied || dataPlane} reload={load}
                        valueGroup={(data.standards_catalog || []).find((s) => s.standard_id === r.subject)?.value_group} />
                ))}
            </div>

            <div className="space-y-2">
                <h3 className="font-bold text-sm text-white uppercase">Obligations → TCL mappings</h3>
                {data.obligations_proposals.map((r) => (
                    <ProposalRow key={r.subject} row={r} tclIds={data.tcl_ids} gated={dataPlane} reload={load} />
                ))}
            </div>
        </div>
    );
}
